import React, { useCallback, useEffect, useMemo, useState } from 'react';
import SearchInput from 'components/SearchInput/SearchInput';
import Pagination from 'components/Pagination/Pagination';
import AddUncataloguedCardModal, {
  AddUncataloguedSelection,
} from 'components/EditHaveListCardModal/AddUncataloguedCardModal';
import { AssetUrls } from 'config/assets';
import { useDebouncedValue } from 'hooks/useDebouncedValue';
import { useFetchPublicWantListQuery } from 'store/services/collection/publicCollectionApi';
import { useAddCardToHaveListMutation } from 'store/services/collectionList/haveList/haveListApi';
import {
  useGetReceiveCandidatesQuery,
  useGetSendCandidatesQuery,
} from 'store/services/tradeOffers/tradeOffersApi';
import { CollectionList } from 'types/interfaces/collectionList';
import {
  OfferCandidateRow,
  OfferCandidateWantGroup,
  OfferLineItem,
} from 'types/interfaces/tradeOffer';
import {
  allowedConditionsFor,
  defaultDeclaredCondition,
  priceForCondition,
} from 'utils/offerConditions';
import { formatConditionShort, formatLanguage, parseOfferError } from 'utils/offerErrors';
import { useOfferBuilder, OfferSide } from '../context/OfferBuilderContext';
import { useSendMatchIndex } from '../hooks/useSendMatchIndex';
import { useWantListPriceFill } from '../hooks/useWantListPriceFill';
import OfferCardRow from './OfferCardRow';
import WantListInfoRow from './WantListInfoRow';
import {finishFields, haveFinishFromRow} from 'utils/finishes';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import CardSizeSelector, { CardSize } from 'components/CardSizeSelector/CardSizeSelector';
import CardFilterTokenRow from 'components/FilterPanel/CardFilterTokenRow';
import type { CardFilterStateApi } from 'components/FilterPanel/cardFilterUrlState';
import { formatLanguages } from 'constants/languages';
import type { OfferCandidatesSort } from 'types/interfaces/tradeOffer';
import type { SortOrder } from 'types/interfaces/sort';
import type { FinishCounts } from 'utils/finishFilter';
import { formatConditions, formatFoilPreference, wantArtFinish } from 'utils/wantListHelpers';
import { usePagedAccumulation } from '../hooks/usePagedAccumulation';
import { useRemoveOfferLine } from '../hooks/useRemoveOfferLine';
import SourcePanelGrid from './SourcePanelGrid';
import OfferExpandNudge from './OfferExpandNudge';
import { AUTO_FOCUS_THRESHOLD } from 'utils/offerFocusState';
import type { OfferGridTileProps } from './OfferGridTile';

const PANEL_PAGE_SIZE = 25;
/** Focus-mode grid page (Asana 1219788320590626 ruling 3): Load More accumulation, never above the route cap of 100. */
const FOCUS_PAGE_SIZE = 48;

export type GridSort = OfferCandidatesSort;
const GRID_SORT_OPTIONS: { value: GridSort; label: string }[] = [
  { value: 'value_desc', label: 'Price: high to low' },
  { value: 'value_asc', label: 'Price: low to high' },
  { value: 'name_asc', label: 'Name: A to Z' },
];
/** The public want-list route spells the same three sorts as key + direction. */
const WANT_LIST_SORT: Record<GridSort, { sort: string; direction: SortOrder }> = {
  value_desc: { sort: 'reference_price', direction: 'desc' },
  value_asc: { sort: 'reference_price', direction: 'asc' },
  name_asc: { sort: 'card_name', direction: 'asc' },
};

export type SourceMode = 'intersection' | 'full';

/**
 * The want record a send-side row satisfies. Passed as one object rather than
 * loose ids so the binding and the budget can never drift apart at a call site.
 * Undefined on the receive side, which has no want context at all.
 */
type WantContext = OfferCandidateWantGroup['wantEntry'];

/**
 * Send-side rows carry the recipient's accepted conditions and the
 * per-condition quote map; the line defaults to the row's own condition when
 * accepted (untouched selector = today's payload), else the closest accepted
 * declaration, priced from the map. Receive-side rows pass no want context.
 *
 * The want binding travels onto the line here — not just for un-catalogued
 * adds — because it is what makes three printings of one wanted card share a
 * single allowance instead of each carrying its own.
 */
const rowToLineItem = (row: OfferCandidateRow, wantEntry?: WantContext): OfferLineItem => {
  const acceptedConditions = wantEntry?.acceptedConditions;
  const allowed = allowedConditionsFor(row.condition, acceptedConditions);
  const declared = defaultDeclaredCondition(row.condition, allowed) ?? row.condition;
  return {
    userMTGCardHaveId: row.userMTGCardHaveId,
    quantity: 1,
    ownedQuantity: row.ownedQuantity,
    // Unaddable rows never reach here, so a priced condition always exists
    unitPricePoints: (priceForCondition(row, declared) ?? row.quotedPricePoints) as number,
    ...finishFields(haveFinishFromRow(row)),
    condition: row.condition,
    declaredCondition: declared,
    quotedPricesByCondition: row.quotedPricesByCondition,
    rowQuotedPricePoints: row.quotedPricePoints,
    acceptedConditions,
    ...(wantEntry
      ? { originatingWantEntryId: wantEntry.id, wantEntryQuantity: wantEntry.quantity }
      : {}),
    card: row.card,
  };
};

interface SourcePanelProps {
  /** 'send' = their want list (fills You send); 'receive' = their have list (fills They send) */
  side: 'send' | 'receive';
  /**
   * Raised when the candidates fetch is rejected with PROPOSER_OFFERS_DISABLED
   * — the VIEWER has trade offers turned off, so the builder cannot even load.
   * The page owns the remedy (the enable-in-place dialog); this panel only
   * reports it, and suppresses its own error copy so the dialog is the single
   * thing on screen.
   */
  onProposerOffersDisabled?: () => void;
  /**
   * Focus mode (Asana 1219788320590626): the panel owns the width and renders
   * the list as a filterable tile grid. All optional, so the compact
   * four-column panel is byte-identical to before.
   */
  focused?: boolean;
  /** Decision 14 phone layout: S/L sizes, 44px targets, no Expand button. */
  phone?: boolean;
  /** This side's filters; applied to the queries ONLY while focused (Decision 6). */
  filters?: CardFilterStateApi;
  gridSize?: CardSize;
  onGridSizeChange?: (size: CardSize) => void;
  /** Header "Expand" (Decision 2). */
  onExpand?: () => void;
  /** Header "Show both sides" (Decision 4). */
  onShowBoth?: () => void;
  /** The Full have/want list toggle was clicked; carries that list's total for the auto-focus rule (Decision 9). */
  onFullListSelected?: (fullListTotal: number | undefined) => void;
  /** Mirrors the Matches / Full toggle to the page (the strip needs it for Decision 10). */
  onModeChange?: (mode: SourceMode) => void;
  /**
   * The mode a fresh instance opens on. The phone focus view and the step
   * panels remount this component, so the page hands back the mode it last
   * mirrored: tapping Full have list must open the grid ON the full list.
   */
  initialMode?: SourceMode;
  /** The active list's facet counts (the rail's Finish group, Decision 7) and filtered total. */
  onResultMeta?: (meta: { finishCounts?: FinishCounts; totalCount?: number }) => void;
}

/**
 * One source panel, two instances. Opens on the intersection (never the raw
 * list — a real profile runs to 5,010 have rows) with a visible escape hatch
 * to the full list. Search and pagination are server-side: filter first,
 * paginate second.
 */
const SourcePanel: React.FC<SourcePanelProps> = ({
  side,
  onProposerOffersDisabled,
  focused = false,
  phone = false,
  filters,
  gridSize = 'S',
  onGridSizeChange,
  onExpand,
  onShowBoth,
  onFullListSelected,
  onModeChange,
  onResultMeta,
  initialMode = 'intersection',
}) => {
  const {
    counterpartyId,
    counterpartyUsername,
    youSend,
    theySend,
    addItem,
    getAddedQuantity,
    getWantEntryAddedQuantity,
    markRowCreatedByBuilder,
    youSendAtCap,
    theySendAtCap,
  } = useOfferBuilder();

  const targetSide: OfferSide = side === 'send' ? 'youSend' : 'theySend';
  const atCap = side === 'send' ? youSendAtCap : theySendAtCap;
  const sideItems = targetSide === 'youSend' ? youSend : theySend;
  // The overlay's arrow points at the cart the card lands in: They send sits
  // to the LEFT of their have list, You send to the RIGHT of their want list.
  const addArrow: 'left' | 'right' = side === 'receive' ? 'left' : 'right';
  // The grid's "✓ Added" removes through the cart's own path (undo rules included).
  const removeLine = useRemoveOfferLine(targetSide);

  const [mode, setMode] = useState<SourceMode>(initialMode);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [gridSort, setGridSort] = useState<GridSort>('value_desc');
  const pageSize = focused ? FOCUS_PAGE_SIZE : PANEL_PAGE_SIZE;
  // The rail's filters reach the server only in focus mode; the compact view
  // ignores them while they wait in memory for the session (Decision 6).
  const filterArgs = focused ? filters?.requestArgs : undefined;
  const hasActiveFilters = focused && !!filters?.hasActiveFilters;

  // Un-catalogued add: the want entry whose "which copy do you own?" control
  // is open. Which entries are already added is NOT tracked here — it is derived
  // from the package below, so removing the line reverts the button.
  const [addingWant, setAddingWant] = useState<CollectionList | null>(null);
  const [addCardToHaveList] = useAddCardToHaveListMutation();

  const debouncedSearch = useDebouncedValue(searchTerm, 300);
  // Clearing the field bypasses the debounce so one click restores the list
  const effectiveSearch = searchTerm === '' ? '' : debouncedSearch;

  useEffect(() => {
    setPage(1);
  }, [effectiveSearch, mode, filterArgs, gridSort, pageSize]);

  // Full mode differs by side: offer-candidates implements mode=full for
  // side=receive only. The send panel's full mode is the counterparty's
  // PUBLIC WANT LIST (informational; no privacy gate exists for want lists),
  // with viewer ownership joined in client-side from the intersection.
  const isFullWantList = side === 'send' && mode === 'full';

  // sort=value_desc on the DISPLAY queries only. The count probes below stay
  // unsorted: they read totalCount alone, and a differing arg would just spawn
  // a second cache entry for the same number.
  const sendQuery = useGetSendCandidatesQuery(
    {
      userId: counterpartyId,
      mode: 'intersection',
      page,
      limit: pageSize,
      search: effectiveSearch.trim() || undefined,
      sort: focused ? gridSort : 'value_desc',
      filters: filterArgs,
    },
    { skip: side !== 'send' || isFullWantList }
  );
  const receiveQuery = useGetReceiveCandidatesQuery(
    {
      userId: counterpartyId,
      mode,
      page,
      limit: pageSize,
      search: effectiveSearch.trim() || undefined,
      sort: focused ? gridSort : 'value_desc',
      filters: filterArgs,
    },
    { skip: side !== 'receive' }
  );
  const wantListQuery = useFetchPublicWantListQuery(
    {
      username: counterpartyUsername,
      page,
      limit: pageSize,
      search: effectiveSearch.trim() || undefined,
      // Focus mode only: the grid's sort, the card filters, and pricedFirst so
      // unpriced wants sort last under price asc exactly as offer-candidates'
      // value_asc does (ruling 13). Sent under value_asc only: the want-list
      // route applies the prefix under whatever sort it gets, while the
      // candidates route applies it under value_asc alone, so sending it under
      // name_asc would split the send grid into priced A-Z then unpriced A-Z.
      // The compact view sends none of these.
      ...(focused
        ? {
            ...WANT_LIST_SORT[gridSort],
            filters: filterArgs,
            pricedFirst: gridSort === 'value_asc',
          }
        : {}),
    },
    { skip: !isFullWantList }
  );

  // Focus mode: Load More accumulation keyed by everything that changes the
  // result set (ruling 3); the compact view renders the current page only.
  const signature = [
    side,
    mode,
    effectiveSearch.trim(),
    gridSort,
    pageSize,
    JSON.stringify(filterArgs ?? null),
  ].join('|');
  const receiveAccumulated = usePagedAccumulation(signature, page, receiveQuery.data?.candidates);
  const sendAccumulated = usePagedAccumulation(signature, page, sendQuery.data?.candidates);
  const wantAccumulated = usePagedAccumulation(signature, page, wantListQuery.data?.wantList);
  const receiveList = focused ? receiveAccumulated : receiveQuery.data?.candidates ?? [];
  const sendList = focused ? sendAccumulated : sendQuery.data?.candidates ?? [];
  const wantListPage = wantListQuery.data?.wantList;
  const wantList = useMemo(
    () => (focused ? wantAccumulated : wantListPage ?? []),
    [focused, wantAccumulated, wantListPage]
  );
  const activeHasMore =
    side === 'receive'
      ? !!receiveQuery.data?.hasMore
      : isFullWantList
        ? !!wantListQuery.data?.hasMore
        : !!sendQuery.data?.hasMore;
  const finishCounts =
    side === 'receive'
      ? receiveQuery.data?.finishCounts
      : isFullWantList
        ? wantListQuery.data?.finishCounts
        : sendQuery.data?.finishCounts;

  useEffect(() => {
    onModeChange?.(mode);
  }, [mode, onModeChange]);

  // Ownership index over the whole intersection: which want entries the
  // viewer can actually add, and with which have rows (decisions 2-4).
  const matchIndex = useSendMatchIndex(counterpartyId, isFullWantList);

  // Tiny limit-1 count queries: the subtitle always shows the match count and
  // the full-list toggle is labeled with its real total (decision 15). The
  // send side's full total is the public want list's totalRecords (unique
  // entries, matching rendered rows), not an offer-candidates count.
  const countArgs = { userId: counterpartyId, page: 1, limit: 1 };
  const sendMatchCount = useGetSendCandidatesQuery(
    { ...countArgs, mode: 'intersection' as const },
    { skip: side !== 'send' }
  );
  const receiveMatchCount = useGetReceiveCandidatesQuery(
    { ...countArgs, mode: 'intersection' as const },
    { skip: side !== 'receive' }
  );
  const wantListTotalQuery = useFetchPublicWantListQuery(
    { username: counterpartyUsername, page: 1, limit: 1 },
    { skip: side !== 'send' }
  );
  // Every card the counterparty marked for trade, whatever their profile
  // Have List visibility says (Asana 1219001672830918: forTrade is the
  // builder's availability rule; the profile setting governs the profile tab
  // only, and the backend no longer 403s mode=full on it).
  const receiveFullCount = useGetReceiveCandidatesQuery(
    { ...countArgs, mode: 'full' as const },
    { skip: side !== 'receive' }
  );
  const matchCount = (side === 'send' ? sendMatchCount : receiveMatchCount).data?.totalCount;
  const fullListTotal =
    side === 'send'
      ? wantListTotalQuery.data?.totalRecords
      : receiveFullCount.data?.totalCount;

  // Batched display prices for unowned want rows without a displayPrice.
  // Informational only — addable rows always price from quotedPricePoints.
  const unownedRows = useMemo(
    () =>
      isFullWantList && matchIndex.isReady && wantListQuery.data
        ? wantList.filter((entry) => !matchIndex.index.has(entry.id))
        : undefined,
    [isFullWantList, matchIndex.isReady, matchIndex.index, wantListQuery.data, wantList]
  );
  const priceMap = useWantListPriceFill(unownedRows);

  const isLoading =
    side === 'receive'
      ? receiveQuery.isLoading
      : isFullWantList
        ? wantListQuery.isLoading || matchIndex.isLoading
        : sendQuery.isLoading;
  const isFetching =
    side === 'receive'
      ? receiveQuery.isFetching
      : isFullWantList
        ? wantListQuery.isFetching || matchIndex.isLoading
        : sendQuery.isFetching;
  const error =
    side === 'receive' ? receiveQuery.error : isFullWantList ? wantListQuery.error : sendQuery.error;
  const hasData =
    side === 'receive'
      ? !!receiveQuery.data
      : isFullWantList
        ? !!wantListQuery.data
        : !!sendQuery.data;

  // Full-want-mode displayed count: the public endpoint returns no
  // filteredCount, so during a search derive the filtered total from the page
  // in hand (exact once hasMore is false) — the displayed count must always
  // agree with rendered rows, and pagination must not offer empty pages.
  const wantData = wantListQuery.data;
  const fullWantCount =
    wantData == null
      ? 0
      : wantData.filteredCount ??
        (effectiveSearch.trim()
          ? wantData.hasMore
            ? wantData.totalRecords
            : (page - 1) * pageSize + wantData.wantList.length
          : wantData.totalRecords);

  // If the result set shrinks under us, fall back to the last valid page
  const totalCount =
    side === 'receive'
      ? receiveQuery.data?.totalCount ?? 0
      : isFullWantList
        ? fullWantCount
        : sendQuery.data?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const resultTotal = hasData ? totalCount : undefined;
  useEffect(() => {
    if (focused) onResultMeta?.({ finishCounts, totalCount: resultTotal });
  }, [focused, finishCounts, resultTotal, onResultMeta]);
  useEffect(() => {
    if (hasData && page > 1 && page > totalPages) {
      setPage(totalPages);
    }
  }, [hasData, page, totalPages]);

  const parsedError = useMemo(() => (error ? parseOfferError(error) : null), [error]);
  // A backend older than the forTrade rule still 403s mode=full with
  // PRIVATE_HAVE_LIST. Then the Full toggle is not offered rather than shown
  // as a dead control; the count probe is the cheapest place to learn it.
  const fullListUnavailable =
    side === 'receive' &&
    !!receiveFullCount.error &&
    parseOfferError(receiveFullCount.error).kind === 'private_have_list';

  // The viewer's OWN setting blocks the fetch (reciprocity). Hand it to the page
  // so the enable-in-place dialog can offer the fix and then refetch; rendering
  // an error here as well would put a dead-end message behind the dialog.
  const isProposerOffersDisabled = parsedError?.kind === 'proposer_offers_disabled';
  useEffect(() => {
    if (isProposerOffersDisabled) onProposerOffersDisabled?.();
  }, [isProposerOffersDisabled, onProposerOffersDisabled]);

  /**
   * Un-catalogued add. Creates the have row the offer line needs, then seeds
   * the line from that row.
   *
   * `forTrade: false` is mandatory and must be explicit: the backend defaults
   * it to `true` when omitted, which would publish the sender's card to every
   * forTrade-gated surface. Adding one card to one offer must not advertise it
   * to the whole site.
   *
   * The created row is not in the candidates cache yet — invalidation is in
   * flight — so the line is built from the add response rather than waiting
   * for a refetch. Its price comes from the row's own backend-computed
   * `displayPrice`; nothing here estimates a price.
   */
  const handleUncataloguedAdd = useCallback(
    async (want: CollectionList, selection: AddUncataloguedSelection) => {
      const row = await addCardToHaveList({
        cardId: selection.cardId,
        quantity: 1,
        condition: selection.condition,
        // finish is authoritative; the derived foil rides along through Track D
        ...finishFields(haveFinishFromRow(selection)),
        language: selection.language,
        forTrade: false,
        source: 'offer-builder-want-list',
      }).unwrap();

      const price = row.displayPrice;
      if (price == null) {
        // The row exists but cannot be priced, so it cannot be part of an
        // offer. Say so rather than adding an unpriceable line the backend
        // would reject at send time with OFFER_UNPRICEABLE_ITEM.
        throw new Error(
          'This card was added to your have list, but it has no price data and cannot be included in an offer.'
        );
      }

      // Built from the SELECTED printing, not the want entry's default card:
      // for an any-set want those are usually different printings, and the
      // line must show the copy the sender actually said they own. Assembled
      // field by field rather than cast — MTGCard carries neither `scryfallId`
      // nor `imageUrl`, so a cast would silently render an empty image.
      const { printing } = selection;
      const card: OfferLineItem['card'] = {
        id: printing.id,
        oracleId: want.oracleId ?? want.card?.oracleId ?? null,
        scryfallId: printing.scryfallID || null,
        name: want.cardName || want.card?.name || '',
        setCode: printing.setCode,
        setName: printing.setName,
        collectorNumber: printing.collectorNumber,
        lang: printing.lang,
        imageUrl: printing.scryfallID
          ? AssetUrls.cards.getCardImage(printing.scryfallID, 'normal')
          : null,
      };

      addItem(targetSide, {
        userMTGCardHaveId: row.id,
        quantity: 1,
        ownedQuantity: row.quantity,
        unitPricePoints: price,
        ...finishFields(haveFinishFromRow(row)),
        condition: row.condition,
        declaredCondition: row.condition,
        rowQuotedPricePoints: price,
        acceptedConditions: want.acceptableConditions,
        // Binds the line back to the want row that spawned it, which is what
        // lets that row show "Added" without a second copy of the state, and
        // charges it against that want's allowance like any other line.
        originatingWantEntryId: want.id,
        wantEntryQuantity: want.quantity,
        card,
      });

      // Pass the server's post-add quantity, not a boolean: the context owns
      // the create-vs-increment rule so the "never delete a pre-existing row"
      // guarantee lives in exactly one place.
      markRowCreatedByBuilder(row.id, row.quantity, 1);
    },
    [addCardToHaveList, addItem, targetSide, markRowCreatedByBuilder]
  );

  /**
   * The add gates, computed once for a row so the compact row's button and
   * the focus grid's overlay cannot disagree (QA round 1, Decision 4).
   */
  const addGateFor = (row: OfferCandidateRow, wantEntry?: WantContext) => {
    const acceptedConditions = wantEntry?.acceptedConditions;
    const added = getAddedQuantity(targetSide, row.userMTGCardHaveId) > 0;
    // Send side: a row is addable only if at least one condition the recipient
    // accepts has price data (add-time blocking, not propose-time — the whole
    // point of the condition ticket). Receive side keeps the plain price gate.
    const allowed =
      side === 'send' ? allowedConditionsFor(row.condition, acceptedConditions) : null;
    const conditionBlocked = allowed != null && allowed.length === 0;
    const noPrice =
      allowed != null
        ? !conditionBlocked && !allowed.some((c) => priceForCondition(row, c) != null)
        : row.quotedPricePoints == null;

    // The want budget is shared by every printing under this group, so a
    // sibling row can be spent-out without this row itself being added. That
    // is the whole bug: gating on `added` alone let 3 copies of a card they
    // want 1 of into the offer, one printing at a time.
    const spent = wantEntry ? getWantEntryAddedQuantity(targetSide, wantEntry.id) : 0;
    const wantExhausted = wantEntry != null && !added && spent >= wantEntry.quantity;

    const disabled = added || wantExhausted || noPrice || conditionBlocked || atCap;
    // "Added" for the spent-out siblings too — the recipient's allowance is
    // filled, which is what the label is really reporting.
    const label = added || wantExhausted ? 'Added' : 'Add';
    const reason = noPrice
      ? 'No price data for this card, so it can’t be added to an offer'
      : wantExhausted && wantEntry
        ? // Without this a disabled "Added" on a row they never clicked reads
          // as a bug rather than as the rule doing its job.
          `They only want ${wantEntry.quantity}, and you've already added ${spent}. Remove one to swap printings.`
        : atCap && !added
          ? 'This side is at the 100-card limit'
          : undefined;
    // Visible, not a tooltip: the reason names the accepted conditions
    const note =
      conditionBlocked && acceptedConditions
        ? `They accept ${acceptedConditions.map(formatConditionShort).join(', ')} only`
        : undefined;
    return { added, disabled, label, reason, note };
  };

  const renderAddAction = (row: OfferCandidateRow, wantEntry?: WantContext) => {
    const gate = addGateFor(row, wantEntry);
    return (
      <div className="offer-add-action">
        <button
          type="button"
          className="offer-add-btn"
          disabled={gate.disabled}
          title={gate.reason}
          onClick={() => addItem(targetSide, rowToLineItem(row, wantEntry))}
        >
          {gate.label}
        </button>
        {gate.note && <div className="offer-add-reason">{gate.note}</div>}
      </div>
    );
  };

  /**
   * "· they want 2 · 1 left" — the budget made visible before the user hits
   * it, mirroring the ProfileWantListTab treatment (`{remaining} left`) rather
   * than inventing a second vocabulary for the same idea.
   */
  const renderWantGroupQty = (wantEntry: WantContext) => {
    const spent = getWantEntryAddedQuantity(targetSide, wantEntry.id);
    const remaining = wantEntry.quantity - spent;
    return (
      <span className="offer-want-group-qty">
        {' '}
        · they want {wantEntry.quantity}
        {spent > 0 && (
          <span
            className={
              remaining <= 0 ? 'offer-want-group-filled' : 'offer-want-group-remaining'
            }
          >
            {remaining <= 0 ? ' · filled' : ` · ${remaining} left`}
          </span>
        )}
      </span>
    );
  };

  /** The want budget as a tile caption: "they want 2 · 1 left". */
  const wantBudgetText = (wantEntry: WantContext) => {
    const spent = getWantEntryAddedQuantity(targetSide, wantEntry.id);
    const remaining = wantEntry.quantity - spent;
    return `they want ${wantEntry.quantity}${
      spent > 0 ? (remaining <= 0 ? ' · filled' : ` · ${remaining} left`) : ''
    }`;
  };

  type GridTile = OfferGridTileProps & { key: string | number };

  // Focus-mode tiles (Decision 5; overlay per QA round 1 Decision 4). The
  // gates come from addGateFor, so the add rules cannot differ between rows
  // and tiles: a spent-out sibling shows a disabled Add to Trade with the
  // same tooltip the row carries (it is not in the cart, so nothing to
  // remove); an added row's "✓ Added" removes it through the cart's path.
  const tileForRow = (
    row: OfferCandidateRow,
    wantEntry?: WantContext,
    extra?: Partial<OfferGridTileProps>
  ): GridTile => {
    const gate = addGateFor(row, wantEntry);
    return {
    key: row.userMTGCardHaveId,
    imageKey: row.userMTGCardHaveId,
    card: {
      name: row.card.name,
      imageUrl: row.card.imageUrl,
      scryfallId: row.card.scryfallId,
      setCode: row.card.setCode,
      collectorNumber: row.card.collectorNumber,
    },
    foil: row.foil,
    finish: row.finish,
    meta: `${row.card.setCode?.toUpperCase() ?? ''} #${row.card.collectorNumber ?? ''} · ${formatConditionShort(
      row.condition
    )} · ${formatLanguage(row.card.lang)}`,
    pricePoints: row.quotedPricePoints,
    quantityBadge: row.ownedQuantity,
    added: gate.added,
    onAdd: () => addItem(targetSide, rowToLineItem(row, wantEntry)),
    onRemove: () => removeLine({ userMTGCardHaveId: row.userMTGCardHaveId }),
    addDisabledReason: gate.added ? undefined : gate.reason ?? gate.note,
    addDisabledNote: gate.note,
    addArrow,
    ...extra,
    };
  };

  // A full-want-list entry the viewer holds no have row for: the same facts
  // and the same add-uncatalogued path as WantListInfoRow.
  const tileForWantEntry = (entry: CollectionList): GridTile => {
    const name = entry.cardName || entry.card?.name || 'Unknown card';
    const editionLabel =
      entry.isAnySet || !entry.acceptableSetCodes || entry.acceptableSetCodes.length === 0
        ? 'Any set'
        : `${entry.acceptableSetCodes[0].toUpperCase()}${
            entry.acceptableSetCodes.length > 1 ? ` +${entry.acceptableSetCodes.length - 1}` : ''
          }`;
    const added = getWantEntryAddedQuantity(targetSide, entry.id) >= entry.quantity;
    const reason = atCap ? 'This side is at the 100-card limit' : undefined;
    // "✓ Added" removes every line this want entry spawned (the un-catalogued
    // add binds each line to the entry), through the cart's path so a row
    // this session created is undone exactly as the cart's × would.
    const removeEntryLines = () => {
      sideItems
        .filter((item) => item.originatingWantEntryId === entry.id)
        .forEach((item) => {
          void removeLine(item);
        });
    };
    return {
      key: `want-${entry.id}`,
      imageKey: entry.id,
      card: {
        name,
        imageUrl: entry.card?.imageUris?.normal || entry.card?.normal_fallbacks?.[0] || null,
        scryfallId: entry.card?.scryfallID || null,
      },
      artFinish: wantArtFinish(entry),
      meta: `${editionLabel} · ${formatConditions(entry.acceptableConditions)} · ${formatLanguages(
        entry.acceptableLanguages
      )} · ${formatFoilPreference(entry)}`,
      pricePoints: entry.displayPrice ?? priceMap[entry.id] ?? null,
      quantityBadge: entry.quantity,
      caption: added ? 'Added to your have list' : 'Not on your have list yet',
      added,
      onAdd: () => setAddingWant(entry),
      onRemove: removeEntryLines,
      addDisabledReason: added ? undefined : reason,
      addArrow,
    };
  };

  const renderGrid = (tiles: GridTile[]) => (
    <SourcePanelGrid
      size={gridSize}
      tiles={tiles}
      totalCount={totalCount}
      hasMore={activeHasMore}
      isFetching={isFetching}
      onLoadMore={() => setPage((p) => p + 1)}
      phone={phone}
    />
  );

  // The title is what teaches the counterintuitive pairing: it states
  // ownership in words, not whose list it is (decision 15).
  const title = side === 'send' ? 'Cards they want that you own' : 'Cards they own that you want';
  const listNoun = side === 'send' ? 'want list' : 'have list';
  const subtitle = `${counterpartyUsername}'s ${listNoun}${
    matchCount != null
      ? ` · ${matchCount.toLocaleString()} ${matchCount === 1 ? 'match' : 'matches'}`
      : ''
  }`;
  const fullListLabel = `Full ${listNoun}${
    fullListTotal != null ? ` (${fullListTotal.toLocaleString()})` : ''
  }`;

  const unfilteredTotal = mode === 'intersection' ? matchCount : fullListTotal;

  const showFullToggle = side === 'send' || !fullListUnavailable;
  // The one empty message about the counterparty's have list: nothing marked
  // for trade at all (Decision 4). Never a sentence about privacy.
  const nothingForTrade = side === 'receive' && receiveFullCount.data?.totalCount === 0;

  return (
    <section className="offer-source-panel" aria-label={title} aria-busy={isFetching}>
      <header className="offer-source-header">
        <div className="offer-source-title-row">
          <div className="offer-source-title-block">
            <h3 className="offer-source-title">{title}</h3>
            <div className="offer-source-subtitle">{subtitle}</div>
          </div>
          {!phone && !focused && onExpand && (
            <button
              type="button"
              className="offer-source-expand"
              onClick={onExpand}
              data-testid={`offer-expand-${side}`}
            >
              <OpenInFullIcon fontSize="inherit" aria-hidden="true" />
              Expand
            </button>
          )}
          {!phone && focused && onShowBoth && (
            <button
              type="button"
              className="offer-source-expand"
              onClick={onShowBoth}
              data-testid={`offer-show-both-${side}`}
            >
              <CloseFullscreenIcon fontSize="inherit" aria-hidden="true" />
              Show both sides
            </button>
          )}
        </div>
        <div className="offer-source-modes">
          <button
            type="button"
            className={`offer-mode-btn${mode === 'intersection' ? ' active' : ''}`}
            onClick={() => {
              setMode('intersection');
              onModeChange?.('intersection');
            }}
          >
            Matches
          </button>
          {showFullToggle && (
            <button
              type="button"
              className={`offer-mode-btn${mode === 'full' ? ' active' : ''}`}
              data-testid={`offer-full-toggle-${side}`}
              onClick={() => {
                setMode('full');
                // Reported synchronously, before the auto-focus callback: on
                // phones that callback replaces this panel with the focus view
                // in the same render, so the mode effect below never fires for
                // 'full' and the fresh instance would open on Matches.
                onModeChange?.('full');
                onFullListSelected?.(fullListTotal);
              }}
            >
              {fullListLabel}
            </button>
          )}
        </div>
        {focused ? (
          <>
            <div className="offer-source-toolbar">
              <SearchInput
                value={searchTerm}
                onChange={setSearchTerm}
                placeholder="Search cards…"
                ariaLabel={`Search ${title}`}
                className="offer-source-toolbar-search"
              />
              <select
                className="offer-grid-sort"
                value={gridSort}
                onChange={(e) => setGridSort(e.target.value as GridSort)}
                aria-label="Sort"
                data-testid={`offer-grid-sort-${side}`}
              >
                {GRID_SORT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <CardSizeSelector
                value={gridSize}
                onChange={(size) => onGridSizeChange?.(size)}
                sizes={phone ? ['S', 'L'] : undefined}
                className="offer-source-size"
              />
            </div>
            {/* N cards, or N of M cards once a filter or search narrows the
                list: N is the server's filtered total (groups on send Matches,
                ruling 12), M the unfiltered total from the count probes. */}
            <div className="offer-grid-result-line" data-testid={`offer-result-line-${side}`}>
              {hasData
                ? (hasActiveFilters || effectiveSearch.trim()) && unfilteredTotal != null
                  ? `${totalCount.toLocaleString()} of ${unfilteredTotal.toLocaleString()} cards`
                  : `${totalCount.toLocaleString()} ${totalCount === 1 ? 'card' : 'cards'}`
                : ''}
            </div>
            {filters && (
              <CardFilterTokenRow
                state={filters.state}
                tokens={filters.tokens}
                onChange={filters.update}
                onClearToken={filters.clearToken}
                onClearAll={filters.clearAll}
              />
            )}
          </>
        ) : (
          <>
            <div className="offer-source-count">
              {mode === 'intersection' ? 'Matches' : 'Full list'}
              {hasData ? ` (${totalCount.toLocaleString()})` : ''}
            </div>
            <SearchInput
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder="Search cards…"
              ariaLabel={`Search ${title}`}
            />
            {!phone && onExpand && fullListTotal != null && fullListTotal > AUTO_FOCUS_THRESHOLD && (
              <OfferExpandNudge count={fullListTotal} onExpand={onExpand} />
            )}
          </>
        )}
      </header>

      <div className="offer-source-body">
        {isLoading ? (
          <div className="offer-source-status">Loading…</div>
        ) : isProposerOffersDisabled ? (
          // Deliberately quiet: the page's enable-in-place dialog is the remedy
          // and is already on screen. Repeating the rejection behind it would
          // read as a dead end the dialog is actively offering to undo.
          <div className="offer-source-status">Trade offers are turned off for your account.</div>
        ) : parsedError ? (
          <div className="offer-source-status">{parsedError.message}</div>
        ) : isFullWantList ? (
          !wantListQuery.data || !matchIndex.isReady ? (
            <div className="offer-source-status">Loading…</div>
          ) : wantList.length === 0 ? (
            <div className="offer-source-status">
              {effectiveSearch
                ? 'No cards found.'
                : `${counterpartyUsername}'s want list is empty.`}
            </div>
          ) : focused ? (
            renderGrid(
              wantList.flatMap((entry) => {
                const ownedGroup = matchIndex.index.get(entry.id);
                const matches = ownedGroup?.matches;
                return matches && matches.length > 0 && ownedGroup
                  ? matches.map((row) =>
                      tileForRow(row, ownedGroup.wantEntry, {
                        owned: true,
                        caption: wantBudgetText(ownedGroup.wantEntry),
                      })
                    )
                  : [tileForWantEntry(entry)];
              })
            )
          ) : (
            wantList.map((entry) => {
              const ownedGroup = matchIndex.index.get(entry.id);
              const matches = ownedGroup?.matches;
              if (matches && matches.length > 0) {
                // Owned entry: the exact Matches-mode group markup and add
                // path (decision 2), plus the owned marking (decision 4).
                return (
                  <div className="offer-want-group" key={entry.id}>
                    <div className="offer-want-group-header">
                      {entry.cardName || entry.card?.name}
                      {ownedGroup && renderWantGroupQty(ownedGroup.wantEntry)}
                    </div>
                    {matches.map((row) => (
                      <OfferCardRow
                        key={row.userMTGCardHaveId}
                        card={row.card}
                        foil={row.foil}
                  finish={row.finish}
                        condition={row.condition}
                        pricePoints={row.quotedPricePoints}
                        quantityBadge={row.ownedQuantity}
                        owned
                        action={renderAddAction(row, ownedGroup?.wantEntry)}
                      />
                    ))}
                  </div>
                );
              }
              return (
                <WantListInfoRow
                  key={entry.id}
                  item={entry}
                  calculatedPrice={priceMap[entry.id]}
                  onAdd={setAddingWant}
                  addDisabledReason={atCap ? 'This side is at the 100-card limit' : undefined}
                  // The BUDGET, not mere presence: a want of 2 stays addable
                  // after the first copy. Same rule as the catalogued rows, so
                  // the two paths cannot disagree about when a want is filled.
                  added={
                    getWantEntryAddedQuantity(targetSide, entry.id) >= entry.quantity
                  }
                />
              );
            })
          )
        ) : side === 'send' && sendQuery.data ? (
          sendList.length === 0 ? (
            <div className="offer-source-status">
              {mode === 'intersection'
                ? effectiveSearch
                  ? 'No matching cards found.'
                  : `You don't own any cards on ${counterpartyUsername}'s want list yet.`
                : 'No cards found.'}
            </div>
          ) : focused ? (
            renderGrid(
              sendList.flatMap((group) =>
                group.matches.map((row) =>
                  tileForRow(row, group.wantEntry, { caption: wantBudgetText(group.wantEntry) })
                )
              )
            )
          ) : (
            sendList.map((group) => (
              <div className="offer-want-group" key={group.wantEntry.id}>
                <div className="offer-want-group-header">
                  {group.wantEntry.cardName}
                  {renderWantGroupQty(group.wantEntry)}
                </div>
                {group.matches.length === 0 ? (
                  <div className="offer-want-unowned">
                    You don&apos;t own this card, so it can&apos;t be added, but they want it.
                  </div>
                ) : (
                  group.matches.map((row) => (
                    <OfferCardRow
                      key={row.userMTGCardHaveId}
                      card={row.card}
                      foil={row.foil}
                  finish={row.finish}
                      condition={row.condition}
                      pricePoints={row.quotedPricePoints}
                      quantityBadge={row.ownedQuantity}
                      action={renderAddAction(row, group.wantEntry)}
                    />
                  ))
                )}
              </div>
            ))
          )
        ) : side === 'receive' && receiveQuery.data ? (
          receiveList.length === 0 ? (
            <div className="offer-source-status" data-testid="offer-receive-empty">
              {effectiveSearch
                ? mode === 'intersection'
                  ? 'No matching cards found.'
                  : 'No cards found.'
                : nothingForTrade || mode === 'full'
                  ? `${counterpartyUsername} has no cards marked for trade right now.`
                  : `None of your want list cards are on ${counterpartyUsername}'s have list yet.`}
            </div>
          ) : focused ? (
            renderGrid(
              receiveList.map((row) =>
                tileForRow(row, undefined, { onYourList: !!row.matchedWantIds?.length })
              )
            )
          ) : (
            receiveList.map((row) => (
              <OfferCardRow
                key={row.userMTGCardHaveId}
                card={row.card}
                foil={row.foil}
                  finish={row.finish}
                condition={row.condition}
                pricePoints={row.quotedPricePoints}
                quantityBadge={row.ownedQuantity}
                action={renderAddAction(row)}
              />
            ))
          )
        ) : null}
      </div>

      {!focused && hasData && totalPages > 1 && (
        <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
      )}

      <AddUncataloguedCardModal
        isOpen={addingWant != null}
        want={addingWant}
        onClose={() => setAddingWant(null)}
        onConfirm={(selection) => handleUncataloguedAdd(addingWant as CollectionList, selection)}
      />
    </section>
  );
};

export default SourcePanel;
